import { FC } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, FormProvider, SubmitHandler, FieldPath } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Content } from '@/components/layout/Content';
import { LoadingPage } from '@/components/LoadingPage';
import { NotFound } from '@/components/NotFound';
import { useNotify } from '@/hooks/useNotify';
import { useQuery } from '@/hooks/graphql/useQuery';
import { useMutation } from '@/hooks/graphql/useMutation';
import { eachValidationErrorsForHookFrom, ServerValidationError } from '@/hooks/graphql/error/ServerValidationError';
import { Form } from '../edit/parts/Form/Form';
import {
  REGULAR_HEALTH_CHECKUP_REPORT,
  RegularHealthCheckupReportQueryVariables,
  RegularHealthCheckupReportQueryResult,
  COPY_REGULAR_HEALTH_CHECKUP_REPORT,
  CopyRegularHealthCheckupReportQueryVariables,
  CopyRegularHealthCheckupReportQueryResult,
  generateDefaultValues,
  Office,
  Prefecture,
  BusinessType,
  convertFormDataToQueryVariables
} from './query';
import { Schema, schema } from '../edit/schema';

interface SearchParams {
  id: string;
}

export const CopyReport: FC = () => {
  const { id } = useParams<SearchParams>();

  const { data, loading } = useQuery<RegularHealthCheckupReportQueryResult, RegularHealthCheckupReportQueryVariables>(
    REGULAR_HEALTH_CHECKUP_REPORT,
    {
      variables: { id },
      fetchPolicy: 'network-only'
    }
  );
  const regularHealthCheckupReport = data?.client?.regularHealthCheckupReport;

  if (!loading && !regularHealthCheckupReport) {
    return <NotFound />;
  }

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">定期健康診断結果報告書</h1>
      </div>
      <Content size="xl">
        {loading || !regularHealthCheckupReport ? (
          <LoadingPage />
        ) : (
          <FormWrapper
            id={id}
            defaultValues={generateDefaultValues(regularHealthCheckupReport)}
            offices={data?.client.offices}
            prefectures={data?.prefectures}
            businessTypes={data?.businessTypes}
          />
        )}
      </Content>
    </>
  );
};

interface FormWrapperProps {
  id: string;
  defaultValues: Schema;
  offices?: Office[];
  prefectures?: Prefecture[];
  businessTypes?: BusinessType[];
}

const FormWrapper: FC<FormWrapperProps> = ({ id, defaultValues, offices, prefectures, businessTypes }) => {
  const history = useHistory();
  const notify = useNotify();

  const methods = useForm<Schema>({
    defaultValues,
    resolver: yupResolver(schema)
  });
  const { handleSubmit, setError } = methods;

  const [copyRegularHealthCheckupReport] = useMutation<
    CopyRegularHealthCheckupReportQueryResult,
    CopyRegularHealthCheckupReportQueryVariables
  >(COPY_REGULAR_HEALTH_CHECKUP_REPORT);

  const onSubmit: SubmitHandler<Schema> = async data => {
    try {
      const response = await copyRegularHealthCheckupReport({
        variables: convertFormDataToQueryVariables(data, id)
      });
      const newId = response.data?.copyRegularHealthCheckupReport.regularHealthCheckupReport.id;

      notify('保存しました', 'success');
      history.push(`/regular_health_checkup/reports/${newId}`);
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema, data, (path, error) => {
          setError(path as FieldPath<Schema>, error);
        });
        return;
      }
      throw err;
    }
  };

  return (
    <FormProvider {...methods}>
      <Form
        reportId={id}
        onSubmit={handleSubmit(onSubmit)}
        offices={offices}
        prefectures={prefectures}
        businessTypes={businessTypes}
      />
    </FormProvider>
  );
};
